import * as React from "react";
import { ApplicationsService, LanguagesService, LltsLanguage, MtBillingType } from "gen/clients";
import { useMutation, useQuery } from "react-query";
import { Button, Drawer, Stack, Box, Typography } from "@mui/material";
import { DrawerHeader } from "components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "components/DrawerBody/DrawerBody";
import { Form, Formik } from "formik";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { DrawerActions } from "components/DrawerActions/DrawerActions";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { PageLoading } from "components/PageLoading/PageLoading";
import { SelectManyAutocompleteField } from "components/formikFields/SelectManyAutocompleteField/SelectManyAutocompleteField";
import { SelectOption } from "@mui/base";
import { SwitchField } from "../../../../../../components/formikFields/SwitchField/SwitchField";
import { LabelValue } from "../../../../../../components/LabelValue/LabelValue";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import {
  mtBillingTypeOptionsForPreset,
  allMtFileTypeOptions,
  PriceByRating,
  DEFAULT_PRICE_BY_RATING
} from "../../utils/MtUtils";
import { useDialogState } from "hooks/useDialogState";
import { CreatePresetDialog } from "./CreatePresetDialog";
import { RenamePresetDialog } from "./RenamePresetDialog";
import { DeletePresetDialog } from "./DeletePresetDialog";

interface Props {
  applicationId: string;
  onClose: () => void;
  onSuccess: () => void;
}

enum FieldName {
  selectedPreset = "selectedPreset",
  enableTextTranslation = "enableTextTranslation",
  enableFileTranslation = "enableFileTranslation",
  enableHumanEscalation = "enableHumanEscalation",
  enableHumanEscalationPricing = "enableHumanEscalationPricing",
  humanEscalationPriceRating1 = "humanEscalationPriceRating1",
  humanEscalationPriceRating2 = "humanEscalationPriceRating2",
  humanEscalationPriceRating3 = "humanEscalationPriceRating3",
  humanEscalationPriceRating4 = "humanEscalationPriceRating4",
  humanEscalationPriceRating5 = "humanEscalationPriceRating5",
  enableAiLinguisticQualityReview = "enableAiLinguisticQualityReview",
  aiLinguisticQualityReviewSampleSize = "aiLinguisticQualityReviewSampleSize",
  aiLinguisticQualityReviewDocumentSamplePageCount = "aiLinguisticQualityReviewDocumentSamplePageCount",
  showAiLinguisticQualityScorePerFile = "showAiLinguisticQualityScorePerFile",
  enableAiPostEditing = "enableAiPostEditing",
  enableAiToolbox = "enableAiToolbox",
  showCreateTaskButton = "showCreateTaskButton",
  showPostEditAction = "showPostEditAction",
  showWatermarkAction = "showWatermarkAction",
  enableMediaTranslation = "enableMediaTranslation",
  maxRequestsPerUserPerDay = "maxRequestsPerUserPerDay",
  maxCharactersPerRequest = "maxCharactersPerRequest",
  supportedLanguages = "supportedLanguages",
  maxFileSizeMB = "maxFileSize",
  maxFilesPerRequest = "maxFilesPerRequest",
  maxFileRequestsPerClientPerMonth = "maxFileRequestsPerClientPerMonth",
  billingType = "billingType",
  maxAudioFileSizeMB = "maxAudioFileSize",
  maxVideoFileSizeMB = "maxVideoFileSize",
  allowedFileTypes = "allowedFileTypes"
}

interface FormValues {
  [FieldName.selectedPreset]: string;
  [FieldName.enableTextTranslation]: boolean;
  [FieldName.enableFileTranslation]: boolean;
  [FieldName.enableHumanEscalation]: boolean;
  [FieldName.enableHumanEscalationPricing]: boolean;
  [FieldName.humanEscalationPriceRating1]: string;
  [FieldName.humanEscalationPriceRating2]: string;
  [FieldName.humanEscalationPriceRating3]: string;
  [FieldName.humanEscalationPriceRating4]: string;
  [FieldName.humanEscalationPriceRating5]: string;
  [FieldName.enableAiLinguisticQualityReview]: boolean;
  [FieldName.aiLinguisticQualityReviewSampleSize]: number;
  [FieldName.aiLinguisticQualityReviewDocumentSamplePageCount]: number;
  [FieldName.showAiLinguisticQualityScorePerFile]: boolean;
  [FieldName.enableAiPostEditing]: boolean;
  [FieldName.enableAiToolbox]: boolean;
  [FieldName.showCreateTaskButton]: boolean;
  [FieldName.showPostEditAction]: boolean;
  [FieldName.showWatermarkAction]: boolean;
  [FieldName.enableMediaTranslation]: boolean;
  [FieldName.maxRequestsPerUserPerDay]: number;
  [FieldName.maxCharactersPerRequest]: number;
  [FieldName.supportedLanguages]: SelectOption<string>[];
  [FieldName.maxFileSizeMB]: number;
  [FieldName.maxFilesPerRequest]: number;
  [FieldName.maxFileRequestsPerClientPerMonth]: string;
  [FieldName.billingType]: string;
  [FieldName.maxAudioFileSizeMB]: number;
  [FieldName.maxVideoFileSizeMB]: number;
  [FieldName.allowedFileTypes]: SelectOption<string>[];
}

const MachineTranslationSettingsPanel: React.FC<Props> = ({ applicationId, onClose, onSuccess }) => {
  const [selectedPresetId, setSelectedPresetId] = React.useState<string>("DEFAULT_PRESET");
  const [isCreatePresetDialogOpen, openCreatePresetDialog, closeCreatePresetDialog] = useDialogState();
  const [isRenamePresetDialogOpen, openRenamePresetDialog, closeRenamePresetDialog] = useDialogState();
  const [isDeletePresetDialogOpen, openDeletePresetDialog, closeDeletePresetDialog] = useDialogState();
  const [isInitialized, setIsInitialized] = React.useState(false);

  const {
    data: application,
    isLoading: isApplicationLoading,
    isFetching: isApplicationFetching,
    error: applicationError,
    refetch: refetchApplication
  } = useQuery(["getMachineTranslationSettings"], {
    queryFn: () => ApplicationsService.getApplication({ id: applicationId }),
    refetchOnMount: true
  });

  const {
    data: lltsLanguages,
    isLoading: areLltsLanguagesLoading,
    error: lltsLanguagesError
  } = useQuery(["getMtLltsLanguages"], {
    queryFn: () =>
      LanguagesService.listLltsLanguages({
        includeDisabled: false,
        azureIdExists: true
      })
  });

  const lltsLanguageById: Record<string, LltsLanguage> = React.useMemo(
    () =>
      (lltsLanguages || []).reduce(
        (prevValue, currentValue) => {
          // eslint-disable-next-line no-param-reassign
          prevValue[currentValue.id] = currentValue;
          return prevValue;
        },
        {} as Record<string, LltsLanguage>
      ),
    [lltsLanguages]
  );

  const languageOptions = React.useMemo(
    () =>
      (lltsLanguages?.map(l => ({ label: l.name, value: l.id })) || []).sort((o1, o2) =>
        o1.label.localeCompare(o2.label)
      ),
    [lltsLanguages]
  );

  const presets = React.useMemo(() => application?.settingPresets || [], [application?.settingPresets]);

  const presetOptions = React.useMemo(() => {
    const options = [{ label: "Default Settings", value: "DEFAULT_PRESET" }];
    presets.forEach(preset => {
      options.push({ label: preset.name, value: preset.id });
    });
    return options;
  }, [presets]);

  const currentPreset = React.useMemo(() => {
    return presets.find(p => p.id === selectedPresetId);
  }, [presets, selectedPresetId]);

  const existingPresetNames = React.useMemo(() => {
    return presets.map(p => p.name);
  }, [presets]);

  const existingPresetIds = React.useMemo(() => {
    return presets.map(p => p.id);
  }, [presets]);

  // Initialize with default preset selection when component loads
  React.useEffect(() => {
    if (application && !isInitialized) {
      // Default to empty string (Default Settings) when component first loads
      setSelectedPresetId("DEFAULT_PRESET");
      setIsInitialized(true);
    }
  }, [application, isInitialized]);

  const {
    mutateAsync,
    isLoading: isMutating,
    error: mutationError
  } = useMutation("updateApplication", {
    onSuccess,
    mutationFn: ApplicationsService.updateApplication
  });

  const initialValues = React.useMemo(() => {
    // Use current preset settings if a preset is selected, otherwise use default settings
    const settingsToUse = currentPreset?.settings || application?.settings;
    const priceByRating = (settingsToUse as any)?.humanEscalationPriceByRating || DEFAULT_PRICE_BY_RATING;

    return {
      [FieldName.selectedPreset]: selectedPresetId,
      [FieldName.billingType]: settingsToUse?.billingType || MtBillingType.FREE,
      [FieldName.enableTextTranslation]: !settingsToUse?.disableTextTranslation,
      [FieldName.enableFileTranslation]: !settingsToUse?.disableFileTranslation,
      [FieldName.enableHumanEscalation]: !settingsToUse?.disableHumanEscalation,
      [FieldName.enableHumanEscalationPricing]: !!settingsToUse?.enableHumanEscalationPricing,
      [FieldName.humanEscalationPriceRating1]: (priceByRating["1"] || 0).toFixed(2),
      [FieldName.humanEscalationPriceRating2]: (priceByRating["2"] || 0).toFixed(2),
      [FieldName.humanEscalationPriceRating3]: (priceByRating["3"] || 0).toFixed(2),
      [FieldName.humanEscalationPriceRating4]: (priceByRating["4"] || 0).toFixed(2),
      [FieldName.humanEscalationPriceRating5]: (priceByRating["5"] || 0).toFixed(2),
      [FieldName.enableAiLinguisticQualityReview]: settingsToUse?.enableAiLinguisticQualityReview || false,
      [FieldName.aiLinguisticQualityReviewSampleSize]: settingsToUse?.aiLinguisticQualityReviewSampleSize || 1000,
      [FieldName.aiLinguisticQualityReviewDocumentSamplePageCount]:
        settingsToUse?.aiLinguisticQualityReviewDocumentSamplePageCount || 5,
      [FieldName.showAiLinguisticQualityScorePerFile]: settingsToUse?.showAiLinguisticQualityScorePerFile || false,
      [FieldName.enableAiPostEditing]: settingsToUse?.enableAiPostEditing || false,
      [FieldName.enableAiToolbox]: settingsToUse?.enableAiToolbox || false,
      [FieldName.showCreateTaskButton]: !settingsToUse?.aiToolboxSettings?.hideCreateTaskButton,
      [FieldName.showPostEditAction]: !settingsToUse?.aiToolboxSettings?.hidePostEditAction,
      [FieldName.showWatermarkAction]: !settingsToUse?.aiToolboxSettings?.hideWatermarkAction,
      [FieldName.enableMediaTranslation]: !settingsToUse?.disableMediaTranslation,
      [FieldName.maxRequestsPerUserPerDay]: settingsToUse?.maxRequestsPerUserPerDay || 0,
      [FieldName.maxCharactersPerRequest]: settingsToUse?.maxCharactersPerRequest || 0,
      [FieldName.maxFileSizeMB]: settingsToUse?.maxFileSizeMB || 0,
      [FieldName.maxAudioFileSizeMB]: settingsToUse?.maxAudioFileSizeMB || 0,
      [FieldName.maxVideoFileSizeMB]: settingsToUse?.maxVideoFileSizeMB || 0,
      [FieldName.maxFilesPerRequest]: settingsToUse?.maxFilesPerRequest || 0,
      [FieldName.maxFileRequestsPerClientPerMonth]: settingsToUse?.maxFileRequestsPerClientPerMonth
        ? `${settingsToUse.maxFileRequestsPerClientPerMonth}`
        : "",
      [FieldName.supportedLanguages]: (
        settingsToUse?.supportedLltsLanguages
          ?.filter(langId => lltsLanguageById[langId] && !lltsLanguageById[langId].isDisabled)
          .map(langId => ({
            label: lltsLanguageById[langId]?.name || langId,
            value: langId
          })) || []
      ).sort((o1, o2) => o1.label.localeCompare(o2.label)),
      [FieldName.allowedFileTypes]:
        settingsToUse?.allowedFileTypes === undefined
          ? allMtFileTypeOptions // If undefined, show all file types as selected
          : allMtFileTypeOptions.filter(option => settingsToUse.allowedFileTypes!.includes(option.value as any))
    };
  }, [application, lltsLanguageById, currentPreset, selectedPresetId]);

  const validate = React.useCallback((formValues: FormValues) => {
    const errors: Record<string, string> = {};
    if (!formValues[FieldName.enableTextTranslation] && !formValues[FieldName.enableFileTranslation]) {
      errors[FieldName.enableTextTranslation] = "Either Text or File translation must be enabled";
      errors[FieldName.enableFileTranslation] = "Either Text or File translation must be enabled";
    }

    // Validate AI Linguistic Quality Review sample size
    if (formValues[FieldName.enableAiLinguisticQualityReview]) {
      const sampleSize = formValues[FieldName.aiLinguisticQualityReviewSampleSize];
      if (!sampleSize || sampleSize <= 0) {
        errors[FieldName.aiLinguisticQualityReviewSampleSize] = "Sample size must be a positive number";
      } else if (sampleSize > 100000) {
        errors[FieldName.aiLinguisticQualityReviewSampleSize] = "Sample size cannot exceed 100,000 characters";
      } else if (!Number.isInteger(sampleSize)) {
        errors[FieldName.aiLinguisticQualityReviewSampleSize] = "Sample size must be a whole number";
      }

      // Validate AI Linguistic Quality Review document sample page count
      const pageCount = formValues[FieldName.aiLinguisticQualityReviewDocumentSamplePageCount];
      if (!pageCount || pageCount <= 0) {
        errors[FieldName.aiLinguisticQualityReviewDocumentSamplePageCount] = "Page count must be a positive number";
      } else if (!Number.isInteger(pageCount)) {
        errors[FieldName.aiLinguisticQualityReviewDocumentSamplePageCount] = "Page count must be a whole number";
      }
    }

    // Validate maximum file requests per client per month
    const maxFileRequests = formValues[FieldName.maxFileRequestsPerClientPerMonth];
    if (maxFileRequests && maxFileRequests !== "") {
      const numValue = parseInt(maxFileRequests, 10);
      if (isNaN(numValue) || numValue <= 0) {
        errors[FieldName.maxFileRequestsPerClientPerMonth] = "Must be a positive number";
      }
    }

    // Validate human escalation prices
    if (formValues[FieldName.enableHumanEscalation] && formValues[FieldName.enableHumanEscalationPricing]) {
      const priceFields = [
        FieldName.humanEscalationPriceRating1,
        FieldName.humanEscalationPriceRating2,
        FieldName.humanEscalationPriceRating3,
        FieldName.humanEscalationPriceRating4,
        FieldName.humanEscalationPriceRating5
      ];

      priceFields.forEach(field => {
        const priceValue = formValues[field] as string;
        if (priceValue !== undefined && priceValue !== "") {
          const numValue = parseFloat(priceValue);
          if (isNaN(numValue)) {
            errors[field] = "Must be a valid number";
          } else if (numValue < 0) {
            errors[field] = "Price cannot be negative";
          } else if (!/^\d+(\.\d{1,2})?$/.test(priceValue)) {
            errors[field] = "Price can have maximum 2 decimal places";
          }
        }
      });
    }

    return errors;
  }, []);

  const onCreatePresetSuccess = React.useCallback(async () => {
    // Refresh application data to get the updated presets
    await refetchApplication();
    closeCreatePresetDialog();
  }, [refetchApplication, closeCreatePresetDialog]);

  const onPresetCreated = React.useCallback((presetId: string) => {
    // Switch to the newly created preset
    setSelectedPresetId(presetId);
  }, []);

  const onRenamePresetSuccess = React.useCallback(async () => {
    // Refresh application data to get the updated presets
    await refetchApplication();
    closeRenamePresetDialog();
  }, [refetchApplication, closeRenamePresetDialog]);

  const onDeletePresetSuccess = React.useCallback(async () => {
    // Refresh application data to get the updated presets
    await refetchApplication();
    closeDeletePresetDialog();
  }, [refetchApplication, closeDeletePresetDialog]);

  const onPresetDeleted = React.useCallback(() => {
    // Switch to default preset if the deleted preset was currently selected
    if (selectedPresetId !== "DEFAULT_PRESET") {
      setSelectedPresetId("DEFAULT_PRESET");
    }
  }, [selectedPresetId]);

  const onSubmit = React.useCallback(
    async (formValues: FormValues) => {
      if (!application) {
        return;
      }

      const selectedLanguageOptions = formValues[FieldName.supportedLanguages];
      const selectedLanguageIds = selectedLanguageOptions.map(lo => lo.value);
      const selectedFileTypeOptions = formValues[FieldName.allowedFileTypes];
      const selectedFileTypes = selectedFileTypeOptions.map(option => option.value as any);

      // If all file types are selected, save as undefined (default behavior)
      const allowedFileTypesToSave =
        selectedFileTypes.length === allMtFileTypeOptions.length ? undefined : selectedFileTypes;

      const newSettings = {
        billingType: formValues[FieldName.billingType] as MtBillingType,
        disableTextTranslation: !formValues[FieldName.enableTextTranslation],
        disableFileTranslation: !formValues[FieldName.enableFileTranslation],
        disableHumanEscalation: !formValues[FieldName.enableHumanEscalation],
        enableHumanEscalationPricing: formValues[FieldName.enableHumanEscalationPricing],
        humanEscalationPriceByRating: {
          "1": parseFloat(formValues[FieldName.humanEscalationPriceRating1]) || 0,
          "2": parseFloat(formValues[FieldName.humanEscalationPriceRating2]) || 0,
          "3": parseFloat(formValues[FieldName.humanEscalationPriceRating3]) || 0,
          "4": parseFloat(formValues[FieldName.humanEscalationPriceRating4]) || 0,
          "5": parseFloat(formValues[FieldName.humanEscalationPriceRating5]) || 0
        } as PriceByRating,
        enableAiLinguisticQualityReview: formValues[FieldName.enableAiLinguisticQualityReview],
        aiLinguisticQualityReviewSampleSize: formValues[FieldName.aiLinguisticQualityReviewSampleSize],
        aiLinguisticQualityReviewDocumentSamplePageCount:
          formValues[FieldName.aiLinguisticQualityReviewDocumentSamplePageCount],
        showAiLinguisticQualityScorePerFile: formValues[FieldName.showAiLinguisticQualityScorePerFile],
        enableAiPostEditing: formValues[FieldName.enableAiPostEditing],
        enableAiToolbox: formValues[FieldName.enableAiToolbox],
        aiToolboxSettings: {
          hideCreateTaskButton: !formValues[FieldName.showCreateTaskButton],
          hidePostEditAction: !formValues[FieldName.showPostEditAction],
          hideWatermarkAction: !formValues[FieldName.showWatermarkAction]
        },
        disableMediaTranslation: !formValues[FieldName.enableMediaTranslation],
        maxRequestsPerUserPerDay: formValues[FieldName.maxRequestsPerUserPerDay],
        maxCharactersPerRequest: formValues[FieldName.maxCharactersPerRequest],
        supportedLltsLanguages: selectedLanguageIds,
        maxFileSizeMB: formValues[FieldName.maxFileSizeMB],
        maxFilesPerRequest: formValues[FieldName.maxFilesPerRequest],
        maxFileRequestsPerClientPerMonth: formValues[FieldName.maxFileRequestsPerClientPerMonth]
          ? parseInt(formValues[FieldName.maxFileRequestsPerClientPerMonth], 10)
          : undefined,
        maxAudioFileSizeMB: formValues[FieldName.maxAudioFileSizeMB],
        maxVideoFileSizeMB: formValues[FieldName.maxVideoFileSizeMB],
        allowedFileTypes: allowedFileTypesToSave
      };

      let updatedApplication;

      if (selectedPresetId && selectedPresetId !== "DEFAULT_PRESET") {
        // Update the selected preset's settings
        const updatedPresets = (application.settingPresets || []).map(preset =>
          preset.id === selectedPresetId ? { ...preset, settings: newSettings } : preset
        );

        updatedApplication = {
          ...application,
          settingPresets: updatedPresets
        };
      } else {
        // Update default settings
        updatedApplication = {
          ...application,
          settings: newSettings
        };
      }

      await mutateAsync({
        id: application.id,
        requestBody: updatedApplication
      });
    },
    [application, mutateAsync, selectedPresetId]
  );

  const error = applicationError || lltsLanguagesError;
  const isLoading = isApplicationLoading || areLltsLanguagesLoading || isApplicationFetching;

  if (error) {
    return (
      <Drawer open={true} onClose={onClose} anchor="right" PaperProps={{ sx: { width: "80vw" } }}>
        <ApiErrorMessage apiError={error} />
      </Drawer>
    );
  }

  if (isLoading || !application) {
    return (
      <Drawer open={true} onClose={onClose} anchor="right" PaperProps={{ sx: { width: "80vw" } }}>
        <PageLoading />
      </Drawer>
    );
  }

  return (
    <Drawer open={true} onClose={onClose} anchor="right" PaperProps={{ sx: { width: "80vw" } }}>
      {mutationError && <SnackbarApiError error={mutationError} />}
      <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate} enableReinitialize={true}>
        {({ values, resetForm }) => {
          // Handler for preset selection change
          const handlePresetChange = (presetId: string) => {
            setSelectedPresetId(presetId);

            // Get the settings for the selected preset
            const preset = presets.find(p => p.id === presetId);
            const settingsToUse = preset?.settings || application?.settings;
            const priceByRating = (settingsToUse as any)?.humanEscalationPriceByRating || DEFAULT_PRICE_BY_RATING;

            // Create new form values with the preset's settings
            const newFormValues = {
              [FieldName.selectedPreset]: presetId,
              [FieldName.billingType]: settingsToUse?.billingType || MtBillingType.FREE,
              [FieldName.enableTextTranslation]: !settingsToUse?.disableTextTranslation,
              [FieldName.enableFileTranslation]: !settingsToUse?.disableFileTranslation,
              [FieldName.enableHumanEscalation]: !settingsToUse?.disableHumanEscalation,
              [FieldName.enableHumanEscalationPricing]: !!settingsToUse?.enableHumanEscalationPricing,
              [FieldName.humanEscalationPriceRating1]: priceByRating["1"]?.toString() || "0",
              [FieldName.humanEscalationPriceRating2]: priceByRating["2"]?.toString() || "0",
              [FieldName.humanEscalationPriceRating3]: priceByRating["3"]?.toString() || "0",
              [FieldName.humanEscalationPriceRating4]: priceByRating["4"]?.toString() || "0",
              [FieldName.humanEscalationPriceRating5]: priceByRating["5"]?.toString() || "0",
              [FieldName.enableAiLinguisticQualityReview]: settingsToUse?.enableAiLinguisticQualityReview || false,
              [FieldName.aiLinguisticQualityReviewSampleSize]:
                settingsToUse?.aiLinguisticQualityReviewSampleSize || 1000,
              [FieldName.aiLinguisticQualityReviewDocumentSamplePageCount]:
                settingsToUse?.aiLinguisticQualityReviewDocumentSamplePageCount || 5,
              [FieldName.showAiLinguisticQualityScorePerFile]:
                settingsToUse?.showAiLinguisticQualityScorePerFile || false,
              [FieldName.enableAiPostEditing]: settingsToUse?.enableAiPostEditing || false,
              [FieldName.enableAiToolbox]: settingsToUse?.enableAiToolbox || false,
              [FieldName.showCreateTaskButton]: !settingsToUse?.aiToolboxSettings?.hideCreateTaskButton,
              [FieldName.showPostEditAction]: !settingsToUse?.aiToolboxSettings?.hidePostEditAction,
              [FieldName.showWatermarkAction]: !settingsToUse?.aiToolboxSettings?.hideWatermarkAction,
              [FieldName.enableMediaTranslation]: !settingsToUse?.disableMediaTranslation,
              [FieldName.maxRequestsPerUserPerDay]: settingsToUse?.maxRequestsPerUserPerDay || 0,
              [FieldName.maxCharactersPerRequest]: settingsToUse?.maxCharactersPerRequest || 0,
              [FieldName.maxFileSizeMB]: settingsToUse?.maxFileSizeMB || 0,
              [FieldName.maxAudioFileSizeMB]: settingsToUse?.maxAudioFileSizeMB || 0,
              [FieldName.maxVideoFileSizeMB]: settingsToUse?.maxVideoFileSizeMB || 0,
              [FieldName.maxFilesPerRequest]: settingsToUse?.maxFilesPerRequest || 0,
              [FieldName.maxFileRequestsPerClientPerMonth]: settingsToUse?.maxFileRequestsPerClientPerMonth
                ? `${settingsToUse.maxFileRequestsPerClientPerMonth}`
                : "",
              [FieldName.supportedLanguages]: (
                settingsToUse?.supportedLltsLanguages
                  ?.filter(langId => lltsLanguageById[langId] && !lltsLanguageById[langId].isDisabled)
                  .map(langId => ({
                    label: lltsLanguageById[langId]?.name || langId,
                    value: langId
                  })) || []
              ).sort((o1, o2) => o1.label.localeCompare(o2.label)),
              [FieldName.allowedFileTypes]:
                settingsToUse?.allowedFileTypes === undefined
                  ? allMtFileTypeOptions // If undefined, show all file types as selected
                  : allMtFileTypeOptions.filter(option => settingsToUse.allowedFileTypes!.includes(option.value as any))
            };

            // Reset the form with new values
            resetForm({ values: newFormValues });
          };

          return (
            <Form noValidate={true} autoComplete="off">
              <DrawerHeader title="Machine Translation Settings" onClose={onClose} />
              <DrawerBody>
                <Stack spacing={3}>
                  {/* Preset Selection Section */}
                  <Box>
                    <Stack direction="row" spacing={2} alignItems="flex-end">
                      <Box sx={{ flexGrow: 1 }}>
                        <SelectOneField
                          name={FieldName.selectedPreset}
                          label="Settings Preset"
                          options={presetOptions}
                          onChange={handlePresetChange}
                        />
                      </Box>
                      <Button variant="outlined" onClick={openCreatePresetDialog} sx={{ mb: 0.5 }}>
                        Create Preset
                      </Button>
                      {selectedPresetId !== "DEFAULT_PRESET" && (
                        <>
                          <Button variant="outlined" onClick={openRenamePresetDialog} sx={{ mb: 0.5 }}>
                            Rename
                          </Button>
                          <Button variant="outlined" color="error" onClick={openDeletePresetDialog} sx={{ mb: 0.5 }}>
                            Delete
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Box>
                  <Stack spacing={3}>
                    {/* General Settings */}
                    <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        General Settings
                      </Typography>
                      <Stack spacing={3}>
                        <LabelValue
                          label="Text Translation"
                          value={
                            <SwitchField
                              name={FieldName.enableTextTranslation}
                              label={values[FieldName.enableTextTranslation] ? "Enabled" : "Disabled"}
                            />
                          }
                        />
                        <LabelValue
                          label="File Translation"
                          value={
                            <SwitchField
                              name={FieldName.enableFileTranslation}
                              label={values[FieldName.enableFileTranslation] ? "Enabled" : "Disabled"}
                            />
                          }
                        />
                        <LabelValue
                          label="Multimedia Translation"
                          value={
                            <SwitchField
                              name={FieldName.enableMediaTranslation}
                              label={values[FieldName.enableMediaTranslation] ? "Enabled" : "Disabled"}
                            />
                          }
                        />
                        <LabelValue
                          label="Human Escalation"
                          value={
                            <SwitchField
                              name={FieldName.enableHumanEscalation}
                              label={values[FieldName.enableHumanEscalation] ? "Enabled" : "Disabled"}
                            />
                          }
                        />
                        <LabelValue
                          label="AI Linguistic Quality Review"
                          value={
                            <SwitchField
                              name={FieldName.enableAiLinguisticQualityReview}
                              label={values[FieldName.enableAiLinguisticQualityReview] ? "Enabled" : "Disabled"}
                            />
                          }
                        />
                        <LabelValue
                          label="AI Toolbox"
                          value={
                            <SwitchField
                              name={FieldName.enableAiToolbox}
                              label={values[FieldName.enableAiToolbox] ? "Enabled" : "Disabled"}
                            />
                          }
                        />
                        <SelectOneField
                          name={FieldName.billingType}
                          label="MT Billing Type"
                          options={mtBillingTypeOptionsForPreset}
                          required={true}
                        />
                        <TextInputField
                          name={FieldName.maxRequestsPerUserPerDay}
                          label="Maximum number of requests per user per day"
                          type="number"
                          required={true}
                        />
                        <TextInputField
                          name={FieldName.maxFilesPerRequest}
                          label="Maximum number of files per request"
                          type="number"
                          required={true}
                        />
                        <TextInputField
                          name={FieldName.maxFileRequestsPerClientPerMonth}
                          label="Maximum file requests per month for client"
                          type="number"
                          required={false}
                          InputLabelProps={{ shrink: true }}
                        />
                        <SelectManyAutocompleteField
                          name={FieldName.supportedLanguages}
                          label="Supported Languages"
                          options={languageOptions}
                          required={true}
                        />
                      </Stack>
                    </Box>

                    {/* Text Translation Settings */}
                    {values[FieldName.enableTextTranslation] && (
                      <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                          Text Translation Settings
                        </Typography>
                        <Stack spacing={3}>
                          <TextInputField
                            name={FieldName.maxCharactersPerRequest}
                            label="Maximum characters per text translation request"
                            type="number"
                            required={true}
                          />
                          <LabelValue
                            label="AI Text Post Editing"
                            value={
                              <SwitchField
                                name={FieldName.enableAiPostEditing}
                                label={values[FieldName.enableAiPostEditing] ? "Enabled" : "Disabled"}
                              />
                            }
                          />
                        </Stack>
                      </Box>
                    )}

                    {/* File Translation Settings */}
                    {values[FieldName.enableFileTranslation] && (
                      <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                          File Translation Settings
                        </Typography>
                        <Stack spacing={3}>
                          <TextInputField
                            name={FieldName.maxFileSizeMB}
                            label="Maximum document file size (MB)"
                            type="number"
                            required={true}
                          />
                          <SelectManyAutocompleteField
                            name={FieldName.allowedFileTypes}
                            label="Allowed document file types"
                            options={allMtFileTypeOptions}
                          />
                        </Stack>
                      </Box>
                    )}

                    {/* Multimedia Translation Settings */}
                    {values[FieldName.enableMediaTranslation] && (
                      <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                          Multimedia Translation Settings
                        </Typography>
                        <Stack spacing={3}>
                          <TextInputField
                            name={FieldName.maxAudioFileSizeMB}
                            label="Maximum audio file size (MB)"
                            type="number"
                            required={true}
                          />
                          <TextInputField
                            name={FieldName.maxVideoFileSizeMB}
                            label="Maximum video file size (MB)"
                            type="number"
                            required={true}
                          />
                        </Stack>
                      </Box>
                    )}

                    {/* Human Escalation Settings */}
                    {/* TODO: Temporarily disable human escalation settings
                    {values[FieldName.enableHumanEscalation] && (
                      <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                          Human Escalation Settings
                        </Typography>
                        <Stack spacing={2}>
                          <LabelValue
                            label="Provide Price Estimate"
                            value={
                              <SwitchField
                                name={FieldName.enableHumanEscalationPricing}
                                label={values[FieldName.enableHumanEscalationPricing] ? "Enabled" : "Disabled"}
                              />
                            }
                          />
                          {values[FieldName.enableHumanEscalationPricing] && (
                            <>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                Pricing per word
                              </Typography>
                              <Stack direction="row" spacing={2}>
                                <Box sx={{ flex: 1 }}>
                                  <TextInputField
                                    name={FieldName.humanEscalationPriceRating1}
                                    label="1-Star"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    required={true}
                                  />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <TextInputField
                                    name={FieldName.humanEscalationPriceRating2}
                                    label="2-Star"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    required={true}
                                  />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <TextInputField
                                    name={FieldName.humanEscalationPriceRating3}
                                    label="3-Star"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    required={true}
                                  />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <TextInputField
                                    name={FieldName.humanEscalationPriceRating4}
                                    label="4-Star"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    required={true}
                                  />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <TextInputField
                                    name={FieldName.humanEscalationPriceRating5}
                                    label="5-Star"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    required={true}
                                  />
                                </Box>
                              </Stack>
                            </>
                          )}
                        </Stack>
                      </Box>
                    )}
                    */}

                    {/* AI Linguistic Quality Review Settings */}
                    {values[FieldName.enableAiLinguisticQualityReview] && (
                      <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                          AI Linguistic Quality Review Settings
                        </Typography>
                        <Stack spacing={3}>
                          <TextInputField
                            name={FieldName.aiLinguisticQualityReviewSampleSize}
                            label="AI Linguistic Quality Review Sample Size (characters)"
                            type="number"
                            required={true}
                          />
                          <TextInputField
                            name={FieldName.aiLinguisticQualityReviewDocumentSamplePageCount}
                            label="AI Linguistic Quality Review Document Sample Page Count"
                            type="number"
                            required={true}
                          />
                          <LabelValue
                            label="Show AI Linguistic Quality Score Per File"
                            value={
                              <SwitchField
                                name={FieldName.showAiLinguisticQualityScorePerFile}
                                label={values[FieldName.showAiLinguisticQualityScorePerFile] ? "Enabled" : "Disabled"}
                              />
                            }
                          />
                        </Stack>
                      </Box>
                    )}

                    {/* AI Toolbox Settings */}
                    {values[FieldName.enableAiToolbox] && (
                      <Box sx={{ border: 1, borderColor: "grey.300", borderRadius: 1, p: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                          AI Toolbox Settings
                        </Typography>
                        <Stack spacing={3}>
                          <LabelValue
                            label="Show 'Create Task' Button"
                            value={
                              <SwitchField
                                name={FieldName.showCreateTaskButton}
                                label={values[FieldName.showCreateTaskButton] ? "Shown" : "Hidden"}
                              />
                            }
                          />
                          <LabelValue
                            label="Show 'Improve Text' Action"
                            value={
                              <SwitchField
                                name={FieldName.showPostEditAction}
                                label={values[FieldName.showPostEditAction] ? "Shown" : "Hidden"}
                              />
                            }
                          />
                          <LabelValue
                            label="Show 'Add Watermark' Action"
                            value={
                              <SwitchField
                                name={FieldName.showWatermarkAction}
                                label={values[FieldName.showWatermarkAction] ? "Shown" : "Hidden"}
                              />
                            }
                          />
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Stack>
              </DrawerBody>
              <DrawerActions>
                <LoadingButton isLoading={isMutating}>Save</LoadingButton>
                <Button color="secondary" onClick={onClose}>
                  Close
                </Button>
              </DrawerActions>
            </Form>
          );
        }}
      </Formik>
      {isCreatePresetDialogOpen && (
        <CreatePresetDialog
          applicationId={applicationId}
          existingPresetNames={existingPresetNames}
          existingPresetIds={existingPresetIds}
          onClose={closeCreatePresetDialog}
          onRefresh={onCreatePresetSuccess}
          onPresetCreated={onPresetCreated}
        />
      )}
      {isRenamePresetDialogOpen && selectedPresetId && (
        <RenamePresetDialog
          applicationId={applicationId}
          selectedPresetId={selectedPresetId}
          currentPresetName={currentPreset?.name || ""}
          existingPresetNames={existingPresetNames}
          onClose={closeRenamePresetDialog}
          onRefresh={onRenamePresetSuccess}
        />
      )}
      {isDeletePresetDialogOpen && selectedPresetId && selectedPresetId !== "DEFAULT_PRESET" && (
        <DeletePresetDialog
          applicationId={applicationId}
          presetId={selectedPresetId}
          presetName={currentPreset?.name || ""}
          onClose={closeDeletePresetDialog}
          onRefresh={onDeletePresetSuccess}
          onPresetDeleted={onPresetDeleted}
        />
      )}
    </Drawer>
  );
};

export { MachineTranslationSettingsPanel };
